import ReactMarkdown from 'react-markdown';
import {
  REMARK_PLUGINS,
  REHYPE_PLUGINS,
  MARKDOWN_COMPONENTS,
} from '@/components/core/markdown/markdown';
import { FileText } from 'lucide-react';
import type { Source } from '@/data/fixtures';
import { useSourceText } from '@/components/features/learning/sources/useSourceText';
import {
  ViewerLoading,
  ViewerError,
} from '@/components/features/learning/sources/viewers/shell/ViewerStates';
import { ViewerShell } from '@/components/features/learning/sources/viewers/shell/ViewerShell';

interface MarkdownViewerProps {
  source: Source;
}

/**
 * Renders `.md` / `.markdown` source files via ReactMarkdown - same
 * library used in the lesson body, so the styling stays consistent
 * with text-mode lessons. We deliberately keep this minimal (no
 * concept tags, no citations) since this is the *raw* user upload.
 */
export function MarkdownViewer({ source }: MarkdownViewerProps) {
  const { text, isLoading, error } = useSourceText(source.id);

  if (isLoading) return <ViewerLoading />;
  if (error)
    return <ViewerError message="Couldn't load file" detail={error.message} />;

  return (
    <ViewerShell
      sourceId={source.id}
      icon={FileText}
      label='Markdown'
      tone='reference'
    >
      <div className='h-full w-full overflow-auto p-6'>
        <div className='prose prose-base mx-auto max-w-3xl dark:prose-invert prose-headings:font-semibold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-code:rounded-sm prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-code prose-code:font-normal prose-code:text-foreground prose-code:before:content-[""] prose-code:after:content-[""] prose-pre:bg-muted prose-pre:text-foreground prose-table:text-sm prose-th:border prose-th:border-border prose-th:bg-muted prose-th:px-3 prose-th:py-2 prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2'>
          <ReactMarkdown
            remarkPlugins={REMARK_PLUGINS}
            rehypePlugins={REHYPE_PLUGINS}
            components={MARKDOWN_COMPONENTS}
          >
            {text || ''}
          </ReactMarkdown>
        </div>
      </div>
    </ViewerShell>
  );
}
